import { memo, useMemo, useEffect, useRef } from "react";
import {
  Box,
  Breadcrumbs,
  Button,
  Container,
  Grid2,
  Typography,
  CircularProgress,
} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import PropertyCard from "../../Components/PropertyCard/PropertyCard";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { FullPaddingWrapper } from "../../Components/FullPaddingWrapper/FullPaddingWrapper";
import { SEARCH_PATTERN_REGEX } from "../../Configurations/regexFilter";
import { useInfinitePropertyScroll } from "../../Hooks/useInfinitePropertyScroll";
import LoaderOverlay from "../../Components/Loader/Loader";
import PropertyFilters from "../../Components/PropertyFilters/PropertyFilters";
import { useUrlFilters } from "../../Hooks/useUrlFilters";
import MessageUsPanel from "../../Components/MessageForm/MessageUsPanel";

const PropertyCollectionList = memo(() => {
  const location = useLocation();
  const hasRestoredScroll = useRef(false);

  // Use URL-based filter state
  const { filters, setFilters, resetFilters, saveScrollPosition, restoreScrollPosition, page, setPage } = useUrlFilters();

  // Extract filter type, value, and state code from URL
  const { filterType, filterValue, stateCode } = useMemo(() => {
    const pathParts = location.pathname.split("/").filter(Boolean);
    const lastName = pathParts[pathParts.length - 1];
    // First part of the path is typically the state code (e.g., "AL" in "/AL/city/Andalusia")
    const stateCode = pathParts[0] || "";

    if (SEARCH_PATTERN_REGEX.CITY_REGEX.test(location.pathname)) {
      return { filterType: "city", filterValue: lastName, stateCode };
    }
    if (SEARCH_PATTERN_REGEX.STATE_REGEX.test(location.pathname)) {
      return { filterType: "state", filterValue: lastName, stateCode };
    }
    if (SEARCH_PATTERN_REGEX.NEIGHBORHOOD_ID_REGEX.test(location.pathname)) {
      return { filterType: "neighbourhood_id", filterValue: lastName, stateCode };
    }
    if (SEARCH_PATTERN_REGEX.ZIP_CODE_REGEX.test(location.pathname)) {
      return { filterType: "zip", filterValue: lastName, stateCode };
    }
    if (SEARCH_PATTERN_REGEX.COUNTY_REGEX.test(location.pathname)) {
      return { filterType: "county", filterValue: lastName, stateCode };
    }

    return { filterType: "", filterValue: "", stateCode: "" };
  }, [location.pathname]);

  // Build breadcrumb items based on filter type
  const breadcrumbItems = useMemo(() => {
    const items: { label: string; link?: string }[] = [
      { label: "Home", link: "/" },
    ];

    if (filterType === "state") {
      // State archive: Home -> State (text only)
      items.push({ label: filterValue });
    } else if (filterType === "city") {
      // City archive: Home -> State (link) -> City (text only)
      if (stateCode) {
        items.push({ label: stateCode, link: `/${stateCode}/state/${stateCode}` });
      }
      items.push({ label: decodeURIComponent(filterValue) });
    } else if (filterType === "county") {
      // County archive: Home -> State (link) -> County (text only)
      if (stateCode) {
        items.push({ label: stateCode, link: `/${stateCode}/state/${stateCode}` });
      }
      items.push({ label: decodeURIComponent(filterValue) });
    } else if (filterType === "zip") {
      // Zip archive: Home -> State (link) -> Zip (text only)
      if (stateCode) {
        items.push({ label: stateCode, link: `/${stateCode}/state/${stateCode}` });
      }
      items.push({ label: filterValue });
    } else if (filterType === "neighbourhood_id") {
      // Neighbourhood archive: Home -> State (link) -> Neighbourhood (text only)
      if (stateCode) {
        items.push({ label: stateCode, link: `/${stateCode}/state/${stateCode}` });
      }
      items.push({ label: decodeURIComponent(filterValue) });
    }

    return items;
  }, [filterType, filterValue, stateCode]);

  // Build hook options from filters
  const hookOptions = useMemo(
    () => ({
      filterType,
      filterValue,
      minBeds: filters.minBeds !== "0" ? filters.minBeds : undefined,
      minBaths: filters.minBaths !== "0" ? filters.minBaths : undefined,
      minYearBuilt: filters.minYearBuilt > 1800 ? filters.minYearBuilt : undefined,
      maxYearBuilt: filters.maxYearBuilt < new Date().getFullYear() ? filters.maxYearBuilt : undefined,
      sortBy: filters.sortBy,
      // Amenity filters - only pass if true
      hasAttic: filters.hasAttic || undefined,
      hasBasement: filters.hasBasement || undefined,
      hasDeck: filters.hasDeck || undefined,
      hasBalcony: filters.hasBalcony || undefined,
      hasGarage: filters.hasGarage || undefined,
      hasPatio: filters.hasPatio || undefined,
      hasPool: filters.hasPool || undefined,
      hasAirConditioning: filters.hasAirConditioning || undefined,
      // Pagination state from URL
      initialPage: page,
      onPageChange: setPage,
    }),
    [filterType, filterValue, filters, page, setPage]
  );

  // Use infinite scroll hook
  const {
    properties,
    isInitialLoading,
    isFilterLoading,
    isFetchingMore,
    hasMore,
    totalCount,
    error,
    loadMoreRef,
    shouldShowLoadMoreButton,
    loadMore,
  } = useInfinitePropertyScroll(hookOptions);

  // Restore scroll position when properties are loaded
  useEffect(() => {
    if (!isInitialLoading && !isFilterLoading && properties.length > 0 && !hasRestoredScroll.current) {
      // Small delay to ensure DOM is rendered
      const timer = setTimeout(() => {
        restoreScrollPosition();
        hasRestoredScroll.current = true;
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isInitialLoading, isFilterLoading, properties.length, restoreScrollPosition]);

  // Reset scroll restored flag when filters change
  useEffect(() => {
    hasRestoredScroll.current = false;
  }, [location.search]);

  // Handle property card click - save scroll position
  const handlePropertyClick = () => {
    saveScrollPosition();
  };

  // Initial loading state
  if (isInitialLoading) {
    return <LoaderOverlay />;
  }

  // Error state
  if (error) {
    return (
      <Container maxWidth="xl">
        <FullPaddingWrapper>
          <Box pt="25px" textAlign="center">
            <Typography variant="h6" color="error">
              Failed to load properties. Please try again.
            </Typography>
          </Box>
        </FullPaddingWrapper>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <FullPaddingWrapper>
        <Box pt="25px" sx={{ position: "relative" }}>
          {/* Breadcrumbs */}
          {breadcrumbItems.length > 1 && (
            <Breadcrumbs
              separator={<NavigateNextIcon fontSize="small" />}
              aria-label="breadcrumb"
              sx={{ mb: 2 }}
            >
              {breadcrumbItems.map((item, index) =>
                item.link ? (
                  <Typography
                    key={index}
                    component={RouterLink}
                    to={item.link}
                    sx={{
                      color: "primary.main",
                      textDecoration: "none",
                      "&:hover": {
                        textDecoration: "underline",
                      },
                    }}
                  >
                    {item.label}
                  </Typography>
                ) : (
                  <Typography key={index} color="text.primary">
                    {item.label}
                  </Typography>
                )
              )}
            </Breadcrumbs>
          )}

          <PropertyFilters
            filters={filters}
            onFilterChange={setFilters}
            totalCount={totalCount}
          />

          {/* Filter loading overlay */}
          {isFilterLoading && (
            <Box
              sx={{
                position: "absolute",
                top: 80,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(255, 255, 255, 0.8)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10,
                minHeight: "300px",
              }}
            >
              <CircularProgress size={48} />
              <Typography variant="body1" sx={{ mt: 2 }} color="text.secondary">
                Applying filters...
              </Typography>
            </Box>
          )}

          <Grid2 container spacing={2}>
            {/* Property cards */}
            {properties.map((property, index) => {
              const data = property.data;
              const isLoading = property.isLoading || !property.isLoaded;

              return (
                <Grid2
                  key={`${property.id}-${index}`}
                  size={{ xl: 4, lg: 4, md: 6, sm: 6, xs: 12 }}
                  onClick={handlePropertyClick}
                >
                  <PropertyCard
                    id={data?.propertyRefID || property.id}
                    propertyId={data?.propertyId}
                    type="product"
                    isLoading={isLoading}
                    isClaimed={data?.isClaimed}
                    featuredPhoto={data?.featuredPhoto}
                    mls={data?.mls}
                    propertyName={data?.address?.address}
                    bathCount={data?.propertyOverView?.bathrooms || "0"}
                    bedCount={data?.propertyOverView?.bedrooms}
                    stateName={data?.address?.state}
                    stateZip={data?.address?.zip}
                    sqrFeet={data?.propertyOverView?.lotSize}
                    href={`/${data?.propertyId}/property-id/${data?.propertyId}`}
                    city={data?.address?.city}
                  />
                </Grid2>
              );
            })}

            {/* Loading more indicator */}
            {isFetchingMore && (
              <Grid2 size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </Box>
              </Grid2>
            )}

            {/* Load More button (shown after auto-load limit) */}
            {shouldShowLoadMoreButton && !isFetchingMore && (
              <Grid2 size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
                <Box textAlign="center" py={4}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={loadMore}
                    sx={{
                      minWidth: "200px",
                      textTransform: "none",
                      fontWeight: 600,
                    }}
                  >
                    Load More Properties
                  </Button>
                </Box>
              </Grid2>
            )}

            {/* Intersection observer trigger */}
            {hasMore && !shouldShowLoadMoreButton && (
              <Grid2 size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
                <Box ref={loadMoreRef} sx={{ height: "20px", width: "100%" }} />
              </Grid2>
            )}

            {/* End of list */}
            {!hasMore && properties.length > 0 && (
              <Grid2 size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
                <Box textAlign="center" py={4}>
                  <Typography variant="body2" color="text.secondary">
                    You have reached the end of the list
                  </Typography>
                </Box>
              </Grid2>
            )}

            {/* Empty state */}
            {!isInitialLoading && !isFilterLoading && properties.length === 0 && (
              <Grid2 size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
                <Box textAlign="center" py={{ xs: 5, md: 8 }}>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No properties found
                  </Typography>
                  {filterType ? (
                    <>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Try adjusting your filters or search criteria to see more results.
                      </Typography>
                      <Button variant="outlined" onClick={resetFilters} sx={{ textTransform: "none", mb: 5 }}>
                        Reset Filters
                      </Button>
                    </>
                  ) : (
                    // A home link that didn't resolve (no filters to reset).
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                      We couldn't open that home. Try searching for the address again.
                    </Typography>
                  )}
                  <MessageUsPanel
                    align="center"
                    topic="find-home"
                    title="Can't find your home?"
                    intro="Send us the address and we'll find it or add it for you."
                    messageLabel="Address and anything else we should know"
                    messagePlaceholder="e.g. 4512 Ventura Canyon Ave, Sherman Oaks, CA 91423"
                    data-testid="find-home-message"
                  />
                </Box>
              </Grid2>
            )}
          </Grid2>
        </Box>
      </FullPaddingWrapper>
    </Container>
  );
});

export default PropertyCollectionList;
